<template>
  <v-container fluid class="attendance-container pa-0 ma-0">
    <!-- Header Section -->
    <v-row v-if="showHeader" class="pa-0 ma-0 px-3 mb-4">
      <v-col cols="12" class="pa-0">
        <div class="header-section" style="position: relative;">
          <v-btn icon small class="header-close-btn" @click="dismissHeader">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
          <v-container fluid class="px-3">
            <v-row align="center" class="pa-0 ma-0">
              <v-col cols="12" md="8" class="d-flex align-center pa-0">
                <div class="header-icon-wrapper">
                  <v-icon size="32" color="white">mdi-calendar-check</v-icon>
                </div>
                <div class="ml-4">
                  <h1 class="page-title">Attendance Reports</h1>
                  <p class="page-subtitle">
                    Track team attendance and work hours
                  </p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right pa-0">
                <v-chip class="ma-1 feature-chip" outlined>
                  <v-icon left small>mdi-account-multiple</v-icon>
                  Team Tracking
                </v-chip>
                <v-chip v-if="!$store.state.user.permissions.disableExportList" class="ma-1 feature-chip" outlined>
                  <v-icon left small>mdi-file-export</v-icon>
                  Export
                </v-chip>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" class="pa-0">
                <div class="feature-pills-container">
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-clock-outline</v-icon
                    >
                    Check-in/Check-out
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-calendar-range</v-icon
                    >
                    Date Range
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-account-search</v-icon
                    >
                    Member Filter
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white">mdi-camera</v-icon>
                    Photo Verification
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-col>
    </v-row>

    <!-- Filter Section -->
    <v-row class="pa-0 ma-0 px-3">
      <v-col cols="12">
        <v-card class="modern-card customBorderRadius elevation-0">
          <div class="card-header-green"></div>
          <v-card-title>
            <v-icon left color="primary">mdi-filter</v-icon>
            Filters
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="selectedUser"
                  :items="allUsers"
                  item-text="name"
                  item-value="id"
                  label="Members"
                  hide-details="true"
                  outlined
                  dense
                  multiple
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">{{ item.name }}</span>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ selectedUser.length - 1 }} others)</span
                    >
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-dialog
                  ref="dialog"
                  v-model="modal"
                  :return-value.sync="dates"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="dateRange"
                      label="Date Range"
                      prepend-inner-icon="event"
                      hide-details="false"
                      outlined
                      dense
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    color="primarygrad"
                    scrollable
                    range
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="modal = false"
                      >Cancel</v-btn
                    >
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.dialog.save(dates)"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="6" sm="3" md="2" class="d-flex">
                <v-btn
                  color="primary"
                  depressed
                  block
                  style="border-radius: 8px;"
                  @click="load"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
              <v-col v-if="!$store.state.user.permissions.disableExportList" cols="6" sm="3" md="2" class="d-flex">
                <v-btn
                  color="primary"
                  depressed
                  block
                  style="border-radius: 8px;"
                  @click="exportReport"
                >
                  <v-icon left>mdi-file-export</v-icon>
                  Export
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table Section -->
    <v-row class="pa-0 ma-0 px-3">
      <v-col cols="12">
        <v-card class="modern-card customBorderRadius elevation-0">
          <div class="card-header-green"></div>
          <v-card-title>
            <v-icon left color="primary">mdi-table</v-icon>
            Attendance Data
          </v-card-title>
          <v-card-text v-if="loading">
            <v-row
              class="fill-height"
              align-content="center"
              justify="center"
              style="margin: 0px"
            >
              <v-col class="subtitle-1 text-center" cols="12">
                {{ loadingText }}
              </v-col>
              <v-col cols="6">
                <v-progress-linear
                  color="primary accent-4"
                  indeterminate
                  rounded
                  height="4"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text v-else-if="!loading && reports.length > 0" class="pa-0">
            <v-simple-table hide-default-footer class="elevation-0 modern-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center sticky-col">Members</th>
                    <th
                      v-for="(item, i) in rangeOfDates"
                      :key="i"
                      class="text-center"
                    >
                      {{ formatHeaderDate(item) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, i) in reports"
                    :key="i"
                    :class="{ 'row-no-perm': !item.canMakeAttendance }"
                  >
                    <td class="py-3 text-center sticky-col">
                      <v-avatar color="primarygrad" size="40">
                        <v-img
                          v-if="item.pictureId"
                          :src="getImage(item.picture)"
                          alt=""
                        ></v-img>
                        <span v-else class="white--text" style="font-size:16px;font-weight:600;">
                          {{ item.name.substring(0, 1).toUpperCase() }}
                        </span>
                      </v-avatar>
                      <div class="member-name">{{ item.name }}</div>
                    </td>

                    <!-- No-permission overlay (spans all date columns) -->
                    <td
                      v-if="!item.canMakeAttendance"
                      :colspan="item.data.length"
                      class="py-2 px-2 perm-overlay-cell"
                      @click="openPermissionDialog(item)"
                    >
                      <div class="perm-overlay">
                        <div class="perm-overlay-blur"></div>
                        <div class="perm-overlay-content">
                          <v-icon color="#37cf37" size="22" class="mr-2">lock_outline</v-icon>
                          <div class="perm-overlay-text">
                            <div class="perm-overlay-title">Attendance permission off</div>
                            <div class="perm-overlay-sub">Click to enable for {{ item.name }}</div>
                          </div>
                          <v-btn small depressed color="primary" class="ml-3 perm-overlay-btn">
                            Enable
                          </v-btn>
                        </div>
                      </div>
                    </td>

                    <template v-else>
                    <td
                      v-for="(cell, index) in item.data"
                      :key="index"
                      class="py-2 px-1"
                    >
                      <v-card
                        class="att-card"
                        :class="cell.absent == 'absent' ? 'att-card--absent' : 'att-card--present'"
                        flat
                      >
                        <!-- Top: selfie + status pill + location -->
                        <div class="att-head">
                          <div
                            v-if="
                              cell.hasOwnProperty('checkinImage') &&
                                cell.checkinImage != ''
                            "
                            class="att-selfie-wrap"
                          >
                            <v-avatar size="32" class="att-selfie">
                              <v-img
                                :src="getImage(cell.checkinImage)"
                                alt=""
                              ></v-img>
                            </v-avatar>
                          </div>

                          <span
                            class="status-pill"
                            :class="cell.absent == 'absent' ? 'is-absent' : 'is-present'"
                          >
                            <span class="status-dot"></span>
                            {{ cell.absent == "absent" ? "Absent" : "Present" }}
                          </span>

                          <v-tooltip v-if="cell.hasOwnProperty('checkinLocation')" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <button
                                type="button"
                                class="att-loc-btn"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="openLocation(cell.checkinLocation)"
                              >
                                <v-icon size="14" color="green">place</v-icon>
                              </button>
                            </template>
                            <span>View location</span>
                          </v-tooltip>
                        </div>

                        <!-- Bottom: two-column time grid -->
                        <div
                          v-if="typeof cell.absent == 'undefined'"
                          class="att-times"
                        >
                          <div class="att-time-cell">
                            <div class="att-time-head">
                              <v-icon size="13" color="primary">arrow_downward</v-icon>
                              <span>In</span>
                            </div>
                            <div class="att-time-val">
                              {{ $moment(cell.checkin).format("HH:mm") }}
                            </div>
                          </div>
                          <div class="att-time-sep"></div>
                          <div class="att-time-cell">
                            <div class="att-time-head">
                              <v-icon
                                size="13"
                                :color="cell.checkout == 'Not Done' ? 'grey' : 'primary'"
                              >arrow_upward</v-icon>
                              <span>Out</span>
                            </div>
                            <div
                              class="att-time-val"
                              :class="{ 'is-pending': cell.checkout == 'Not Done' }"
                            >
                              <template v-if="cell.checkout != 'Not Done'">{{ $moment(cell.checkout).format("HH:mm") }}</template>
                              <template v-else>—</template>
                            </div>
                          </div>
                        </div>
                      </v-card>
                    </td>
                    </template>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>

          <v-card-text
            v-else-if="!loading && reports.length === 0"
            style="padding: 0px"
          >
            <v-container
              fill-height
              fill-width
              style="text-align: center; width: 80%; display: inline"
            >
              <div style="text-align: center" align="center">
                <div align="center">
                  <v-img
                    :src="summary"
                    style="width: 220px; justify-content: center; opacity: 0.6"
                  ></v-img>
                </div>
                <h3 class="h3">Nothings in Attendance</h3>
              </div>
            </v-container>
          </v-card-text>

          <v-card-actions
            v-if="!loading && reports.length > 0"
            style="text-align: center !important; padding: 0px"
          >
            <v-spacer></v-spacer>
            <div class="v-data-footer">
              <div class="v-data-footer__select">
                Rows per page:
                <div
                  class="v-input v-input--hide-details v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted v-select"
                >
                  <v-select
                    v-model="records"
                    :items="recordsPerPage"
                    style="margin: 5px"
                    @change="load(records, 0)"
                  ></v-select>
                </div>
              </div>
              <div class="v-data-footer__icons-before">
                <v-btn
                  :disabled="page == 1 ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="prevSet"
                >
                  <v-icon :size="24">arrow_left</v-icon>
                </v-btn>
              </div>
              <div class="v-data-footer__pagination">
                {{ `${page}/${pageCount} of ${totalRecords}` }}
              </div>
              <div class="v-data-footer__icons-after">
                <v-btn
                  :disabled="page >= pageCount ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="nextSet"
                >
                  <v-icon :size="24">arrow_right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="openLocationDialog" max-width="500">
      <v-card>
        <v-card-title>Checkin Location</v-card-title>
        <v-card-text>
          <!-- <iframe
            width="100%"
            height="300"
            :src="locData"
            frameborder="0"
            style="border:0;"
            allowfullscreen=""
            aria-hidden="false"
            tabindex="0"
          ></iframe> -->

          <GmapMap
            :center="locData"
            :zoom="18"
            style="width: 100%; height: 300px;"
          >
            <GmapMarker
              :position="locData"
              :clickable="false"
              :icon="'http://maps.google.com/mapfiles/ms/icons/green-dot.png'"
            />
          </GmapMap>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="closeLocation()">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Enable Attendance Permission Dialog -->
    <v-dialog v-model="permissionDialog" max-width="460" persistent>
      <v-card class="perm-dialog">
        <div class="perm-dialog-header">
          <v-icon size="44" color="#ffffff">how_to_reg</v-icon>
        </div>
        <v-card-title class="perm-dialog-title">
          Attendance Permissions
        </v-card-title>
        <v-card-text class="perm-dialog-body">
          <p class="mb-3" style="font-size:13px;">
            Configure attendance settings for
            <strong>{{ permissionUser ? permissionUser.name : "" }}</strong>.
          </p>

          <div class="perm-toggle-list">
            <div class="perm-toggle-row">
              <div class="perm-toggle-info">
                <div class="perm-toggle-title">Mark Attendance</div>
                <div class="perm-toggle-sub">Allow check-in and check-out</div>
              </div>
              <v-switch
                v-model="permissionDraft.canMakeAttendance"
                color="primary"
                hide-details
                inset
                dense
                class="ma-0 pa-0"
              ></v-switch>
            </div>

            <div class="perm-toggle-row">
              <div class="perm-toggle-info">
                <div class="perm-toggle-title">Capture Location</div>
                <div class="perm-toggle-sub">Record GPS at check-in/out</div>
              </div>
              <v-switch
                v-model="permissionDraft.canCaptureLocation"
                color="primary"
                hide-details
                inset
                dense
                class="ma-0 pa-0"
              ></v-switch>
            </div>

            <div class="perm-toggle-row">
              <div class="perm-toggle-info">
                <div class="perm-toggle-title">Capture Photo</div>
                <div class="perm-toggle-sub">Take a selfie at check-in</div>
              </div>
              <v-switch
                v-model="permissionDraft.canCapturePhoto"
                color="primary"
                hide-details
                inset
                dense
                class="ma-0 pa-0"
              ></v-switch>
            </div>

            <div class="perm-toggle-row">
              <div class="perm-toggle-info">
                <div class="perm-toggle-title">Enable Session Lock</div>
                <div class="perm-toggle-sub">Lock app outside session hours</div>
              </div>
              <v-switch
                v-model="permissionDraft.enableSessionLock"
                color="primary"
                hide-details
                inset
                dense
                class="ma-0 pa-0"
              ></v-switch>
            </div>

            <div v-if="permissionDraft.enableSessionLock" class="perm-session-times">
              <v-text-field
                v-model="permissionDraft.sessionStart"
                label="Shift Start Time"
                type="time"
                outlined
                dense
                hide-details
                prepend-inner-icon="schedule"
                class="perm-time-field"
              ></v-text-field>
              <v-text-field
                v-model="permissionDraft.sessionEnd"
                label="Shift End Time"
                type="time"
                outlined
                dense
                hide-details
                prepend-inner-icon="schedule"
                class="perm-time-field"
              ></v-text-field>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-btn text :disabled="permissionSaving" @click="permissionDialog = false">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="permissionSaving"
            @click="enableAttendancePermission"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" multi-line :color="color">
      <h3 style="font-weight: 400; font-size: 13px; text-transform: uppercase">
        {{ snackbarText }}
      </h3>

      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
const _ = require("lodash");
import Papa from "papaparse";
import Blob from "blob";
import fb from "../js/firebase";
// import * as VueGoogleMaps from "vue2-google-maps";

export default {
  components: {
    // // GmapMap,
    // // GmapMarker,
    // ...VueGoogleMaps,
  },
  data() {
    return {
      showHeader: false,
      snackbar: false,
      snackbarText: "",
      color: "primary",
      search: "",
      rangeOfDates: [],
      modal: false,
      dates: [],

      selectedUser: [],
      allUsers: [],
      permissionDialog: false,
      permissionUser: null,
      permissionSaving: false,
      permissionDraft: {
        canMakeAttendance: false,
        canCaptureLocation: false,
        canCapturePhoto: false,
        enableSessionLock: false,
        sessionStart: "",
        sessionEnd: "",
      },
      selectedDispos: [],
      showTable: false,
      contactDetails: [],
      records: 5,
      loading: false,
      loadingText: "",
      reports: [],      recordsPerPage: [5, 10, 20, 30, 50, 100, 150, 200],
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      officeTime: null,
      //locData: "",
      locData: {
        lat: 0,
        lng: 0,
      },

      openLocationDialog: false,
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "fieldList", "teams", "selectedTeam"]),
    summary() {
      return require("../assets/summary.png");
    },
    dateRange() {
      if (!this.dates || this.dates.length === 0) return "";
      const a = moment(this.dates[0]);
      const b = this.dates[1] ? moment(this.dates[1]) : a;
      const lo = a.isBefore(b) ? a : b;
      const hi = a.isBefore(b) ? b : a;
      if (lo.isSame(hi, "day")) return lo.format("DD MMM");
      if (lo.isSame(hi, "year")) {
        if (lo.isSame(hi, "month")) {
          return lo.format("DD") + "–" + hi.format("DD MMM");
        }
        return lo.format("DD MMM") + " – " + hi.format("DD MMM");
      }
      return lo.format("DD MMM YY") + " – " + hi.format("DD MMM YY");
    },
    calcDispo() {
      return _.without(
        _.map(this.dispos, (d) => {
          if (
            d.title == "SMS" ||
            d.title == "LINK" ||
            d.title == "EMAIL" ||
            d.title == "APP" ||
            d.title == "NEW"
          ) {
            //do not pass these values
          } else {
            return d;
          }
        }),
        undefined,
      );
    },
  },
  watch: {
    async selectedTeam() {
      try {
        await this.fetchAccounts(); // get accounts
        this.load(); // get the logs by day wise format
      } catch (e) {
        (this.snackbar = false), (this.snackbarText = "");
        this.color = "#c62828";
        return;
      }
    },
  },
  async created() {
    this.showHeader =
      localStorage.getItem("attendanceNewHeaderDismissed") !== "true";
    this.dates = [
      moment()
        .subtract(6, "days")
        .format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ];
    await this.fetchAccounts();
    await this.load();
    this.officeTime = this.$moment(
      this.$store.state.companyDetails.officeEndTime,
      "HH:mm",
    ).diff(
      this.$moment(this.$store.state.companyDetails.officeStartTime, "HH:mm"),
    );
    this.officeTime = this.$moment.duration(this.officeTime).asMinutes();
  },

  methods: {
    formatHeaderDate(d) {
      return d ? this.$moment(d).format("ddd, DD MMM") : "";
    },
    openPermissionDialog(item) {
      this.permissionUser = item;
      const account = this.getUserById(item.id) || {};
      const perms = account.permissions || {};
      this.permissionDraft = {
        canMakeAttendance: !!perms.canMakeAttendance,
        canCaptureLocation: !!perms.canCaptureLocation,
        canCapturePhoto: !!perms.canCapturePhoto,
        enableSessionLock: !!perms.enableSessionLock,
        sessionStart: account.sessionStart || "",
        sessionEnd: account.sessionEnd || "",
      };
      this.permissionDialog = true;
    },
    async enableAttendancePermission() {
      if (!this.permissionUser || !this.permissionUser.id) return;
      this.permissionSaving = true;
      try {
        const userId = this.permissionUser.id;
        const account = this.getUserById(userId) || {};
        const currentPerms = account.permissions || {};
        const newPerms = Object.assign({}, currentPerms, {
          canMakeAttendance: this.permissionDraft.canMakeAttendance,
          canCaptureLocation: this.permissionDraft.canCaptureLocation,
          canCapturePhoto: this.permissionDraft.canCapturePhoto,
          enableSessionLock: this.permissionDraft.enableSessionLock,
        });
        await this.$http.patch(
          `${this.$store.state.ENDPOINT}/accounts/${userId}`,
          {
            permissions: newPerms,
            sessionStart: this.permissionDraft.sessionStart || null,
            sessionEnd: this.permissionDraft.sessionEnd || null,
          },
        );
        if (account) {
          account.permissions = newPerms;
          account.sessionStart = this.permissionDraft.sessionStart || null;
          account.sessionEnd = this.permissionDraft.sessionEnd || null;
        }
        const row = _.find(this.reports, (r) => r.id === userId);
        if (row) row.canMakeAttendance = newPerms.canMakeAttendance;
        this.permissionDialog = false;
        this.permissionUser = null;
        this.snackbarText = "Attendance permissions updated";
        this.color = "success";
        this.snackbar = true;
      } catch (e) {
        this.snackbarText =
          (e && e.body && e.body.error && e.body.error.message) ||
          "Failed to update permission";
        this.color = "error";
        this.snackbar = true;
      } finally {
        this.permissionSaving = false;
      }
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("attendanceNewHeaderDismissed", "true");
    },
    closeLocation() {
      this.openLocationDialog = false;
      this.locData = {
        lat: 0,
        lng: 0,
      };
    },
    openLocation(location) {
      this.openLocationDialog = true;

      this.locData = {
        lat: location.lat,
        lng: location.lng,
      };

      // this.locData = `https://www.google.com/maps/embed/v1/view?key=AIzaSyBalE4vJmieJt5BqDZsfTS2XbcV0nKu5ng&center=${location.lat},${location.lng}&zoom=15`;
    },
    /** PAGINATION */
    nextSet() {
      this.page++;
      this.load(
        this.records * this.page,
        this.records * this.page - this.records,
      );
    },
    prevSet() {
      this.page--;
      this.load(
        this.records * this.page,
        this.records * this.page - this.records,
      );
    },
    // Fetch Accounts
    async fetchAccounts() {
      this.allUsers = [];

      try {
        var teams = [];
        teams = await this.getTeamIds();

        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teams,
          },
        );
        this.allUsers = response.body;
        if (this.user.role == "Manager" || this.user.role == "Teammanager") {
          this.allUsers.unshift({
            id: "allMembers",
            name: "All",
          });
        }
        if (this.selectedUser.length < 1) {
          this.selectedUser.push(this.allUsers[0].id);
        }
      } catch (e) {
        if (e.body.error && e.body.error.message)
          return this.$swal({ type: "error", text: e.body.error.message });
        else
          return this.$swal({
            type: "error",
            text: "Error while fetching accounts",
          });
      }
    },

    /** Get array of teams, user ids */
    getTeamIds() {
      var ids = [];

      if (this.selectedTeam == "allTeams") {
        ids = _.map(this.teams, "id");
      } else {
        ids.push(this.selectedTeam);
      }

      ids = _.without(ids, "allTeams");
      return ids;
    },
    getUserIds(limit, skip) {
      var users = [];

      if (_.includes(this.selectedUser, "allMembers")) {
        users = _.map(this.allUsers, "id");
      } else {
        users = this.selectedUser;
      }
      users = _.without(users, "allMembers");

      // users = _.remove(users, { id: "allMembers" });

      if (limit == undefined || skip == undefined) {
        (limit = this.records), (skip = 0);
      }

      this.totalRecords = users.length;
      // total pages
      this.pageCount = Math.ceil(this.totalRecords / this.records);
      if (this.page > this.pageCount) {
        this.page = 1; //set default
      }
      // for member wise paginate
      users = users.slice(skip, limit);
      return users;
    },

    // Load dates and reports
    async load(limit, skip) {
      try {
        // Dates Range
        this.loading = true;
        this.loadingText = "Please wait, we are processing your data";
        var dates = [];

        var start = moment(this.dates[0]);
        var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
        if (start > end) {
          var temp = start;
          start = end;
          end = temp;
        }

        var diff = end.diff(start, "days") + 1;

        // if range is greater than 30 days
        if (diff > 31) {
          this.snackbar = true;
          this.snackbarText = "Please select a range of 30 days";
          this.color = "#cc9900";
          return;
        }
        for (let i = 0; i < diff; i++) {
          dates.push(
            moment(start)
              .add(i, "days")
              .format("YYYY-MM-DD"),
          );
        }

        this.reports = [];
        var users;
        var search = "";

        start = moment
          .utc(start, "YYYY-MM-DD")
          .hours(0)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .toDate();

        end = moment
          .utc(end, "YYYY-MM-DD")
          .hours(23)
          .minutes(59)
          .seconds(59)
          .milliseconds(0)
          .toDate();

        //Get users
        users = await this.getUserIds(limit, skip);

        var filter = {
          where: {
            companyId: this.$store.state.companyDetails.id,
            accountId: { inq: users }, // Filter for accounts array
            attendanceDay: {
              between: [start, end],
            },
          },
        };

        // Make the API call using axios and pass the filter object as a query parameter
        var resp = await this.$http.get(
          `${this.$store.state.ENDPOINT}/attendances`,
          { params: { filter: filter } },
        );

        // Assuming you have already fetched the response and stored it in the 'resp' variable
        var responseData = resp.data; // Replace 'resp' with your actual response object

        var attendanceByAccountAndDate = {};

        responseData.forEach((attendance) => {
          var accountId = attendance.accountId;
          var attendanceDay = new Date(attendance.attendanceDay)
            .toISOString()
            .split("T")[0];

          if (!attendanceByAccountAndDate[accountId]) {
            attendanceByAccountAndDate[accountId] = {};
          }

          if (!attendanceByAccountAndDate[accountId][attendanceDay]) {
            attendanceByAccountAndDate[accountId][attendanceDay] = [];
          }

          attendanceByAccountAndDate[accountId][attendanceDay].push(attendance);
        });

        var members = _.uniq(
          _.map(responseData, (r) => {
            return r.accountId;
          }),
        );

        members = _.map(users, (u) => {
          var userExists = _.find(members, u);
          if (userExists) {
            return userExists;
          } else {
            return u;
          }
        });

        var structuredData = _.map(members, (memberId) => {
          var user = this.getUserById(memberId);
          var canMakeAttendance =
            user && user.permissions
              ? !!user.permissions.canMakeAttendance
              : false;

          var memberData = attendanceByAccountAndDate[memberId];

          if (memberData) {
            return {
              id: memberId,
              name: user.name,
              email: user.email,
              picture: user.picture,
              pictureId: user.pictureId,
              canMakeAttendance: canMakeAttendance,
              data: _.map(memberData, (attendance) => {
                if (attendance.length === 0) {
                  return { absent: "absent" };
                } else if (attendance.length === 1) {
                  return {
                    checkinImage: attendance[0].image,
                    checkinLocation: attendance[0].gps,
                    checkin: attendance[0].time || "absent",
                    checkout: "Not Done",
                  };
                } else {
                  return {
                    checkinImage: attendance[0].image,
                    checkinLocation: attendance[0].gps,
                    checkin: attendance[0].time || "absent",
                    checkout:
                      attendance[attendance.length - 1].time || "absent",
                  };
                }
              }),
            };
          } else {
            return {
              id: memberId,
              name: user.name,
              email: user.email,
              picture: user.picture,
              pictureId: user.pictureId,
              canMakeAttendance: canMakeAttendance,
              data: _.map(dates, () => {
                return { absent: "absent" };
              }),
            };
          }
        });

        structuredData.forEach(function(entry) {
          var newData = [];

          // Iterate through each date in dates
          dates.forEach(function(date) {
            // Check if the date exists in entry.data
            var found = false;
            entry.data.forEach(function(dataEntry) {
              if (dataEntry.checkin && dataEntry.checkin.includes(date)) {
                newData.push(dataEntry);
                found = true;
              }
            });

            // If the date is not found, add an "absent" entry
            if (!found) {
              newData.push({ absent: "absent" });
            }
          });

          entry.data = newData; // Update the entry's data
        });

        this.reports = structuredData;

        // Show latest date first
        dates.reverse();
        _.each(this.reports, (r) => {
          if (Array.isArray(r.data)) r.data.reverse();
        });
        this.rangeOfDates = dates;

        this.loading = false;
        this.loadingText = "";
      } catch (e) {
        console.log("Error", e);
        this.loading = false;
        this.loadingText = "Error while loading your data";
      }
    },
    getUserById(id) {
      for (var u in this.allUsers) {
        if (this.allUsers[u].id == id) {
          return this.allUsers[u];
        }
      }
    },

    // Export CSV
    exportReport() {
      try {
        const self = this;
        var exportData = self.reports;
        self.loader = true;
        exportData = _.map(exportData, (d) => {
          var i = 0;
          var payload = [];
          while (i < 1) {
            var y = {
              Name: d.name.toString(),
              Email: d.email || "",
            };
            for (var x in self.rangeOfDates) {
              if (i == 0) {
                // y.Type = "Status";

                if (d.data[x].absent == "absent") {
                  y[moment(self.rangeOfDates[x]).format("YYYY-MM-DD")] =
                    d.data[x].absent;
                } else {
                  y[
                    moment(self.rangeOfDates[x]).format("YYYY-MM-DD")
                  ] = `Check In - ${moment(d.data[x].checkin).format(
                    "HH:mm",
                  )} :: Check Out -  ${
                    d.data[x].checkout == "Not Done"
                      ? "Not Done"
                      : moment(d.data[x].checkout).format("HH:mm")
                  }`;
                }
              }
            }
            i++;
            payload.push(y);
          }
          return payload;
        });
        exportData = _.flattenDepth(exportData);
        if (exportData.length < 1) {
          self.loader = false;
          self.snackbar = true;
          self.snackbarText = "No records to export";
          self.color = "#cc9900";
          return;
        }
        var csv = Papa.unparse(exportData);
        var csvData = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        var csvURL = null;
        if (navigator.msSaveBlob) {
          csvURL = navigator.msSaveBlob(
            csvData,
            `report_${new Date().getTime()}.csv`,
          );
        } else {
          csvURL = window.URL.createObjectURL(csvData);
        }
        var tempLink = document.createElement("a");
        tempLink.href = csvURL;
        tempLink.setAttribute("download", `report_${new Date().getTime()}.csv`);
        tempLink.click();

        self.loader = false;
      } catch (e) {
        console.log(e);
        self.loader = false;
        self.snackbar = true;
        self.snackbarText = "Something went wrong while exporting reports.";
        self.color = "error";
      }
    },
    getImage(pic) {
      return `${this.$store.state.ENDPOINT}/storages/get/${pic}?access_token=${this.$store.getters.user.token}`;
    },
  },
};
</script>

<style scoped>
.attendance-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .attendance-container {
  background-color: #121212 !important;
}

.feature-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(10px);
  font-weight: 500;
}

.modern-card {
  background: var(--v-background-base, white);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
}

.modern-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  width: 100%;
}

.customBorderRadius {
  border-radius: 12px !important;
}

.h3 {
  font-size: 17px;
  font-weight: 400;
  letter-spacing: 0.5px;
}

/* ===== Modern themed table ===== */
.modern-table {
  border-radius: 0 !important;
  background: transparent !important;
}

.modern-table ::v-deep table {
  background: transparent !important;
}

.modern-table ::v-deep thead tr {
  background: #f6faf6;
}

.theme--dark .modern-table ::v-deep thead tr {
  background: #232323 !important;
}

.modern-table ::v-deep thead th {
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #4a4a4a !important;
  border-bottom: 1px solid #e6e6e6 !important;
  white-space: nowrap;
}

.theme--dark .modern-table ::v-deep thead th {
  color: #d0d0d0 !important;
  border-bottom-color: #333333 !important;
}

.modern-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.06) !important;
}

.theme--dark .modern-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.12) !important;
}

.modern-table ::v-deep tbody td {
  border-bottom: 1px solid #efefef !important;
}

.theme--dark .modern-table ::v-deep tbody td {
  border-bottom-color: #2a2a2a !important;
  color: #e0e0e0;
}

.day-cell {
  background: #f6faf6 !important;
  border: 1px solid #e6f1e6;
  border-radius: 10px !important;
  min-width: 130px;
  padding: 8px 6px;
  transition: all 0.2s ease;
}

.day-cell:hover {
  background: #eef9ee !important;
  border-color: #c9e6c9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.12);
}

.theme--dark .day-cell {
  background: #262d27 !important;
  border-color: #2f3a30 !important;
  color: #e0e0e0;
}

.theme--dark .day-cell:hover {
  background: #2c352d !important;
  border-color: #3b4b3c !important;
}

.day-stat {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.4;
  padding: 2px 0;
  color: inherit;
  flex-wrap: wrap;
}

.day-stat .stat-label {
  font-weight: 500;
  white-space: nowrap;
}

.day-divider {
  border-color: rgba(55, 207, 55, 0.18) !important;
  margin: 2px 6px !important;
}

.theme--dark .day-divider {
  border-color: rgba(55, 207, 55, 0.3) !important;
}

/* Attendance cell — modern */
.att-card {
  position: relative;
  min-width: 150px;
  padding: 10px 10px 8px;
  border-radius: 14px !important;
  background: linear-gradient(180deg, #ffffff 0%, #f4faf4 100%) !important;
  border: 1px solid #e6efe6;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.att-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 3px 0 0 3px;
}

.att-card--present::before {
  background: linear-gradient(180deg, #37cf37, #2ba82b);
}

.att-card--absent::before {
  background: linear-gradient(180deg, #ef5350, #c62828);
}

.att-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(55, 207, 55, 0.16);
  border-color: #c9e6c9;
}

.theme--dark .att-card {
  background: linear-gradient(180deg, #262d27 0%, #1f2520 100%) !important;
  border-color: #2f3a30;
  color: #e0e0e0;
}

.theme--dark .att-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
  border-color: #3b4b3c;
}

.att-head {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  padding: 0 0 8px 4px;
}

.att-selfie-wrap {
  padding: 2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #37cf37, #2ba82b);
  flex-shrink: 0;
}

.att-selfie {
  border: 2px solid #ffffff;
}

.theme--dark .att-selfie {
  border-color: #1f2520;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  line-height: 1;
}

.status-pill.is-present {
  background: rgba(55, 207, 55, 0.16);
  color: #1f8a1f;
}

.status-pill.is-absent {
  background: rgba(244, 67, 54, 0.14);
  color: #c62828;
}

.theme--dark .status-pill.is-present {
  background: rgba(55, 207, 55, 0.24);
  color: #8be08b;
}

.theme--dark .status-pill.is-absent {
  background: rgba(244, 67, 54, 0.22);
  color: #ff8a80;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}

.att-loc-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(55, 207, 55, 0.14);
  border: none;
  cursor: pointer;
  outline: none;
  transition: background 0.15s ease, transform 0.15s ease;
}

.att-loc-btn:hover {
  background: rgba(55, 207, 55, 0.28);
  transform: scale(1.08);
}

.theme--dark .att-loc-btn {
  background: rgba(55, 207, 55, 0.22);
}

.att-times {
  display: flex;
  align-items: stretch;
  background: rgba(55, 207, 55, 0.06);
  border-radius: 10px;
  padding: 6px 4px;
}

.theme--dark .att-times {
  background: rgba(55, 207, 55, 0.12);
}

.att-time-cell {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0 4px;
  text-align: center;
}

.att-time-head {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #7a8a7a;
  line-height: 1;
}

.theme--dark .att-time-head {
  color: #9ab09a;
}

.att-time-val {
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1f2a1f;
  line-height: 1.1;
}

.theme--dark .att-time-val {
  color: #f1f5f1;
}

.att-time-val.is-pending {
  color: #b8b8b8;
  font-weight: 500;
}

.att-time-sep {
  width: 1px;
  background: rgba(55, 207, 55, 0.22);
  margin: 2px 0;
}

.theme--dark .att-time-sep {
  background: rgba(55, 207, 55, 0.3);
}

.member-name {
  font-size: 14px;
  font-weight: 500;
  margin-top: 6px;
  color: inherit;
}

/* Sticky Members column + styled scrollbar */
.modern-table ::v-deep .v-data-table__wrapper,
.modern-table ::v-deep > div {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #b7e3b7 transparent;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar,
.modern-table ::v-deep > div::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track,
.modern-table ::v-deep > div::-webkit-scrollbar-track {
  background: transparent;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb,
.modern-table ::v-deep > div::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #6ed46e, #37cf37);
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover,
.modern-table ::v-deep > div::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #37cf37, #2ba82b);
  background-clip: padding-box;
  border: 2px solid transparent;
}

.theme--dark .modern-table ::v-deep .v-data-table__wrapper,
.theme--dark .modern-table ::v-deep > div {
  scrollbar-color: #2ba82b transparent;
}

.theme--dark .modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb,
.theme--dark .modern-table ::v-deep > div::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #2ba82b, #1e7a1e);
  background-clip: padding-box;
}

.modern-table ::v-deep th.sticky-col,
.modern-table ::v-deep td.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
  min-width: 140px;
}

.modern-table ::v-deep thead th.sticky-col {
  z-index: 3;
  background: #f6faf6;
}

.theme--dark .modern-table ::v-deep th.sticky-col,
.theme--dark .modern-table ::v-deep td.sticky-col {
  background: #1e1e1e;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.4);
}

.theme--dark .modern-table ::v-deep thead th.sticky-col {
  background: #232323;
}

.modern-table ::v-deep tbody tr:hover td.sticky-col {
  background: #f1faf1;
}

.theme--dark .modern-table ::v-deep tbody tr:hover td.sticky-col {
  background: #243124;
}

.theme--dark .h3 {
  color: #d0d0d0;
}

@media (max-width: 960px) {
  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .header-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .feature-pills-container {
    gap: 8px;
  }

  .feature-pill {
    padding: 8px 16px;
    font-size: 12px;
  }
}

/* ===== Permission overlay (no canMakeAttendance) ===== */
.perm-overlay-cell {
  position: relative;
  cursor: pointer;
}

.perm-overlay {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 76px;
  border-radius: 12px;
  overflow: hidden;
  background: repeating-linear-gradient(
    45deg,
    rgba(55, 207, 55, 0.05),
    rgba(55, 207, 55, 0.05) 10px,
    rgba(55, 207, 55, 0.1) 10px,
    rgba(55, 207, 55, 0.1) 20px
  );
  border: 1px dashed rgba(55, 207, 55, 0.45);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.perm-overlay:hover {
  border-color: rgba(55, 207, 55, 0.8);
  background: repeating-linear-gradient(
    45deg,
    rgba(55, 207, 55, 0.08),
    rgba(55, 207, 55, 0.08) 10px,
    rgba(55, 207, 55, 0.16) 10px,
    rgba(55, 207, 55, 0.16) 20px
  );
}

.theme--dark .perm-overlay {
  background: repeating-linear-gradient(
    45deg,
    rgba(55, 207, 55, 0.08),
    rgba(55, 207, 55, 0.08) 10px,
    rgba(55, 207, 55, 0.16) 10px,
    rgba(55, 207, 55, 0.16) 20px
  );
  border-color: rgba(55, 207, 55, 0.4);
}

.perm-overlay-blur {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  pointer-events: none;
}

.perm-overlay-content {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 14px;
  z-index: 1;
  width: 100%;
  max-width: 480px;
}

.perm-overlay-text {
  flex: 1;
  min-width: 0;
}

.perm-overlay-title {
  font-size: 13px;
  font-weight: 700;
  color: #1f8a1f;
  line-height: 1.2;
}

.theme--dark .perm-overlay-title {
  color: #8be08b;
}

.perm-overlay-sub {
  font-size: 11px;
  color: #6a6a6a;
  margin-top: 2px;
}

.theme--dark .perm-overlay-sub {
  color: #b0b0b0;
}

.perm-overlay-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.row-no-perm .sticky-col {
  opacity: 0.75;
}

/* Permission dialog */
.perm-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.perm-dialog-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 22px 24px;
  display: flex;
  justify-content: center;
}

.perm-dialog-title {
  font-size: 20px !important;
  font-weight: 700 !important;
  padding: 16px 24px 4px !important;
}

.perm-dialog-body {
  padding: 8px 24px 12px !important;
}

.perm-toggle-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.perm-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f6faf6;
  border: 1px solid #e6efe6;
  border-radius: 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.perm-toggle-row:hover {
  background: #eef9ee;
  border-color: #c9e6c9;
}

.theme--dark .perm-toggle-row {
  background: #262d27;
  border-color: #2f3a30;
}

.theme--dark .perm-toggle-row:hover {
  background: #2c352d;
  border-color: #3b4b3c;
}

.perm-toggle-info {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
}

.perm-toggle-title {
  font-size: 13px;
  font-weight: 600;
  color: #2a2a2a;
  line-height: 1.2;
}

.theme--dark .perm-toggle-title {
  color: #e8e8e8;
}

.perm-toggle-sub {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
}

.theme--dark .perm-toggle-sub {
  color: #a8a8a8;
}

.perm-session-times {
  display: flex;
  gap: 10px;
  padding: 10px 12px 4px;
  animation: permFadeIn 0.2s ease;
}

.perm-session-times .perm-time-field {
  flex: 1;
}

@keyframes permFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
